// src/utils/seasonDates.js
//
// Canonical parsing for the season's DATE-ONLY fields:
//   registrationOpen / registrationClose / seasonStart / seasonEnd,
//   pricing tier startsAt / endsAt, earlyBirdDeadline.
//
// The coach picks a calendar day ("07/27/2026") with no time or timezone
// attached. Historically these were stored as `new Date('2026-07-27')`,
// which JS parses as **UTC midnight** — rendered in any timezone west of
// UTC that shifts to the previous day (the 7/27 → 7/26 bug), and gating
// like `now > registrationClose` closed registration hours early.
//
// Convention going forward:
//   - Date-only values are interpreted at LOCAL MIDNIGHT of the viewer.
//   - Storage writes local-midnight Dates (Firestore turns them into
//     Timestamps); older docs edited via SeasonDetail hold 'YYYY-MM-DD'
//     strings — both shapes normalize here.
//   - Existing prod docs stored at exact UTC midnight are repaired at
//     parse time: the UTC calendar day is re-anchored to local midnight.
//     (None of these fields ever carries a real time-of-day, so an
//     instant landing exactly on UTC midnight is always a date-only
//     value that took the old storage path.)
//   - "Closes"/"ends" boundaries are INCLUSIVE of their calendar day:
//     use endOfSeasonDay() wherever a close/end date is compared to now.

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Normalize Timestamp | {seconds} | Date | number | string → Date at
 *  local midnight for date-only values; null when absent/invalid. */
export function toSeasonDate(v) {
  if (!v) return null;
  if (typeof v === 'string') {
    const s = v.trim();
    if (DATE_ONLY_RE.test(s)) {
      const [y, m, d] = s.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    const parsed = new Date(s);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  let d = null;
  if (v instanceof Date) d = v;
  else if (typeof v.toDate === 'function') d = v.toDate();
  else if (typeof v.seconds === 'number') d = new Date(v.seconds * 1000);
  else if (typeof v === 'number') d = new Date(v);
  if (!d || Number.isNaN(d.getTime())) return null;
  if (
    d.getUTCHours() === 0 && d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0
  ) {
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }
  return d;
}

/** Like toSeasonDate, but date-only values resolve to 23:59:59.999 local
 *  so "Registration Closes 07/31" includes all of the 31st. */
export function endOfSeasonDay(v) {
  const d = toSeasonDate(v);
  if (!d) return null;
  if (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  }
  return d;
}

/** 'YYYY-MM-DD' for <input type="date">, '' when absent. */
export function toDateInputValue(v) {
  const d = toSeasonDate(v);
  if (!d) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}
